import React, { CSSProperties, FC, ReactNode, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'

interface Props {
  scaleFactor?: number
  children?: ReactNode
  onClick?: () => void
  onLongClick?: () => void
  disableHover?: boolean
  style?: CSSProperties
  tabIndex?: number
  disabled?: boolean
  layoutId?: string
}

const LONG_PRESS_THRESHOLD = 500

const Touchable: FC<Props> = ({
  children,
  scaleFactor = 0.01,
  onClick,
  onLongClick,
  disableHover,
  style,
  tabIndex,
  disabled = false,
  layoutId,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout>()
  const clickStart = useRef<number>()

  const onTouchStart = useCallback(() => {
    clickStart.current = Date.now()
    timeoutRef.current = setTimeout(() => {
      onLongClick?.()
    }, LONG_PRESS_THRESHOLD)
  }, [onLongClick])

  const onTouchEnd = () => {
    if (Date.now() - (clickStart.current || 0) < LONG_PRESS_THRESHOLD) {
      onClick?.()
    }
    clearTimeout(timeoutRef.current)
  }

  return (
    <motion.div
      transition={{
        type: 'spring',
        bounce: 0.5,
      }}
      whileHover={disableHover ? undefined : { scale: 1 + scaleFactor }}
      whileTap={{ scale: 1 - scaleFactor }}
      style={{ cursor: disabled ? 'default' : 'pointer', ...style }}
      tabIndex={tabIndex}
      onMouseDown={onTouchStart}
      onMouseUp={onTouchEnd}
      aria-disabled={disabled}
      layoutId={layoutId}
    >
      {children}
    </motion.div>
  )
}

export default Touchable
